import { apiConfig } from "../config/apiConfig";
import { apiRequestForWrapper } from "../api/apiRequest";

/**
 * CCPA Deregister Service
 * Handles CCPA personal information deregistration requests
 * Implements singleton pattern for single instance management
 * 
 * @class Deregister
 * @author Roshan Jose
 * @version 1.0.0
 */
export class Deregister {
    private static instance: Deregister;
    private success: HTMLElement | null = null;
    private error: HTMLElement | null = null;
    
    /**
     * Private constructor to enforce singleton pattern
     * Stores DOM references first, then initializes the deregister functionality
     */
    private constructor() {
        this.domStore();
        this.init();
    }

    /**
     * Initializes the deregister functionality
     * Fetches CCPA parameter from URL and triggers API call if valid
     */
    init(): void {
        const params = this.fetchParams();
        if (params) {
            this.getApiResult(params);
        }
    }

    /**
     * Fetches and validates the CCPA parameter from the URL
     * @returns {string} Sanitized CCPA UUID parameter or empty string if invalid
     */
    fetchParams(): string {
        const fullUrl = globalThis.location.href;
        
        const url = new URL(fullUrl);
        const queryParams = new URLSearchParams(url.search);
        
        return this.sanitizeInput(queryParams.get('ccpa'));
    }

    /**
     * Sanitizes and validates the CCPA parameter input
     * Ensures the input is a valid UUID format for security
     * 
     * @param {string | null} input - The raw CCPA parameter from URL
     * @returns {string} Valid UUID string or empty string if invalid
     * 
     * @security Validates UUID format to prevent injection attacks
     * @security Prevents XSS, SQL injection, and path traversal
     */
    private sanitizeInput(input: string | null): string {
        if (!input) return '';
        
        // Remove trailing slashes and trim whitespace
        const cleanedInput = input.trim().replace(/\/+$/, '');
        
        // Check if cleaning resulted in empty string
        if (!cleanedInput) {
            console.warn('Invalid CCPA parameter format. Expected UUID format.');
            return '';
        }
        
        // UUID validation regex (8-4-4-4-12 format)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        
        if (!uuidRegex.test(cleanedInput)) {
            console.warn('Invalid CCPA parameter format. Expected UUID format.');
            return '';
        }
        
        return cleanedInput;
    }

    /**
     * Stores references to DOM elements for success and error messages
     * Called during initialization to cache DOM queries
     */
    domStore(): void {
        this.success = document.querySelector('.success-message');
        this.error = document.querySelector('.error-message');
    }

    /**
     * Makes PUT request to CCPA deregister API endpoint
     * 
     * @param {string} ccpa - The validated CCPA UUID parameter
     * @returns {Promise<void>}
     * 
     * @throws Will log error and show error message if API call fails
     * @security Validates API key presence before making request
     * @security Uses HTTPS endpoint with API key authentication
     */
    async getApiResult(ccpa: string): Promise<void> {
        try {
            const apiKey = process.env.JS_API_KEY;
            
            if (!apiKey) {
                console.error('API key is missing. Cannot proceed with CCPA deregister request.');
                this.showError();
                return;
            }
            
            const url = `${apiConfig.CCPA_DEREGISTER}${ccpa}`;
            
            const response = await apiRequestForWrapper<{ success?: boolean }>({
                method: 'PUT',
                url: `${url}?apikey=${apiKey}`,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            // If we get a 200 response, treat it as success
            // Empty response or response without success property is considered success
            if (response?.success === false) {
                this.showError();
            } else {
                this.showSuccess();
            }
        } catch (error) {
            console.error('CCPA deregister error:', error);
            this.showError();
        }
    }

    /**
     * Displays success message and hides error message
     * Updates DOM to show successful deregistration
     */
    showSuccess(): void {
        this.success?.classList.remove('hidden');
        this.error?.classList.add('hidden');
    }

    /**
     * Displays error message and hides success message
     * Updates DOM to show deregistration failure
     */
    showError(): void {
        this.error?.classList.remove('hidden');
        this.success?.classList.add('hidden');
    }
    
    /**
     * Gets the singleton instance of the Deregister class
     * Creates new instance if one doesn't exist
     * 
     * @returns {Deregister} The singleton instance
     * @static
     */
    public static getInstance(): Deregister {
        if (!Deregister.instance) {
            Deregister.instance = new Deregister();
        }
        return Deregister.instance;
    }
}

